import Fuse from "fuse.js";
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { SearchIcon } from "../../assets/icons/SearchIcon";
import LOCAL_STORAGE from "../../constants/LocalStorage";
import { ICustomer, IOrder } from "../../utils/data-types";
import "./Search.scss";

interface SearchProps<T> {
  placeholder: string;
  data: T[] | undefined;
  onSearch: (results: T[]) => void;
  type?: "customer" | "order";
}

const Search = <T extends ICustomer | IOrder>({
  placeholder,
  data,
  onSearch,
  type,
}: SearchProps<T>) => {
  const [searchTerm, setSearchTerm] = useState<string>(
    type === "customer"
      ? localStorage.getItem(LOCAL_STORAGE.CUSTOMER_SEARCH_TERM) ?? ""
      : ""
  );
  const [inputPlaceholder, setInputPlaceholder] = useState<string>(placeholder);
  const inputRef = useRef<HTMLInputElement>(null);

  const fuse = useMemo(() => {
    if (type === "customer") {
      return new Fuse<ICustomer>((data as ICustomer[]) ?? [], {
        keys: ["navn", "id", "adresse"],
        threshold: 0.1,
      });
    } else {
      return new Fuse<IOrder>((data as IOrder[]) ?? [], {
        keys: ["ordreid", "deres_ref", "eref"],
        threshold: 0.3,
      });
    }
  }, [data, type]);

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setSearchTerm(value);

      if (value === "") {
        onSearch(data ?? []);
      } else {
        const results = fuse.search(value);
        onSearch(results.map((result) => result.item) as T[]);
      }
    },
    [data, fuse, onSearch]
  );

  useEffect(() => {
    if (type === "customer") {
      localStorage.setItem(LOCAL_STORAGE.CUSTOMER_SEARCH_TERM, searchTerm);
    }
  }, [searchTerm, type]);

  const handleInputFocus = useCallback(() => {
    setInputPlaceholder("");
  }, []);

  const handleInputBlur = useCallback(() => {
    if (!searchTerm) {
      setInputPlaceholder(placeholder);
    }
  }, [placeholder, searchTerm]);

  return (
    <div className="search-container">
      <input
        ref={inputRef}
        type="text"
        placeholder={inputPlaceholder}
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
      />
      <SearchIcon />
    </div>
  );
};

export default Search;
